import { v4 as uuidv4 } from 'uuid';
import { TpCanonicalTypeInfo, TpDataContext, TpDataTableColumn, VALTYPES } from '../../../data/interfaces';

import styles from '../../../styles/global.module.css';
import widgetStyles from '../widget/Widget.module.scss';
import { PLACEHOLDER_OTHER } from './categoricalTools';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getSelectionHighlightColor } from '../../themes/themes';
import { TpChartConfigParams, TpChartDataBinding, getDataTable } from '../charts/chartDataBindings';
import { selectionFilter2String } from '../../../data/selection/selectionUtils';
import { getUserError } from '../../../components/miscUtils';

export function get_guid() {
  return uuidv4();
}

export function getSortIndex(sortData: any[], inverted: boolean): number[] {
  const test_with_index = [];
  for (let i = 0; i < sortData.length; i++) test_with_index.push([sortData[i], i]);
  test_with_index.sort(function (left, right) {
    if (!inverted) {
      if (left[0] < right[0]) return -1;
      if (left[0] > right[0]) return +1;
    } else {
      if (left[0] < right[0]) return +1;
      if (left[0] > right[0]) return -1;
    }
    return 0;
  });
  const indexes = [];
  const test = [];
  for (const j in test_with_index) {
    test.push(test_with_index[j][0]);
    indexes.push(test_with_index[j][1]);
  }
  return indexes;
}

export function createSortIndexes(
  count: number,
  inverted: boolean,
  sortColInfo?: TpDataTableColumn,
  sortDataInput?: any[],
): [number[], number[]] {
  let sortIndex: number[] = [];
  const sortInvIndex: number[] = [];
  for (let nr = 0; nr < count; nr++) {
    sortIndex.push(inverted ? count - 1 - nr : nr);
    sortInvIndex.push(0);
  }
  if (sortDataInput) {
    let sortData;
    if (sortColInfo) {
      if ([VALTYPES.NUMBER, VALTYPES.INTEGER].includes(sortColInfo!.valueType)) {
        const extremeValue = inverted ? -1e99 : 1e99;
        sortData = sortDataInput.map((value) => (isNaN(value) ? extremeValue : value));
      } else {
        sortData = sortDataInput.map((value) => convertColumnDataToDisplayString(sortColInfo!, value));
      }
    } else {
      sortData = sortDataInput;
    }
    sortIndex = getSortIndex(sortData, inverted);
  }
  for (let nr = 0; nr < count; nr++) sortInvIndex[sortIndex[nr]] = nr;
  return [sortIndex, sortInvIndex];
}

export const chartDims = {
  padding: 15,
  headerHeight: 40,
  xAxisHeight: 25,
  xLabelHeight: 15,
  yAxisWidth: 20,
  legendHeight: 38,
};

export function hasSelectedItems(selectedItemsMap: { [itemId: string]: boolean } | null): boolean {
  return !!selectedItemsMap;
}

export function isSelectedItem(selectedItemsMap: { [itemId: string]: boolean } | null, itemId: string): boolean {
  if (!selectedItemsMap) return true;
  return !!selectedItemsMap[itemId];
}

export function getSelectedItemCount(canonicalTypeInfo: TpCanonicalTypeInfo) {
  if (!canonicalTypeInfo.selectedItemsMap) return 0;
  if (!canonicalTypeInfo.restrictedItemsMap) return Object.keys(canonicalTypeInfo.selectedItemsMap).length;
  let count = 0;
  for (const key of Object.keys(canonicalTypeInfo.selectedItemsMap))
    if (canonicalTypeInfo.restrictedItemsMap[key]) count++;
  return count;
}

export function combineStyles(names: string[]) {
  return names.join(' ');
}

/**
 * Escape RegExp special characters in a string
 * Is the currently most accepted set of characters to escape in JS:
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#escaping
 *
 * Since May 2025, RegExp.escape is supported in all major browsers, but not yet in the standard.
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp/escape
 *
 * @param {string} string - The input string to escape.
 * @returns {string} The escaped string safe for use in RegExp patterns.
 */
export default function escapeRegExp(string: string): string {
  return string.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&').replace(/-/g, '\\x2d');
}

/**
 * Split a text into parts based on multiple search words.
 * Each part is marked as matching or not matching any of the search words.
 * @param {string} text - The original string to split.
 * @param {string} needle - The multi-word search string (e.g., "hello world test").
 * @returns {Array<{text: string, isMatch: boolean}>} Array of structured parts.
 */
function splitTextSearchWords(text: string, needle: string): Array<{ text: string; isMatch: boolean }> {
  if (!needle) return [{ text, isMatch: false }];

  // 1. Prepare search terms and lookup set
  const rawSearchWords = needle.toLowerCase().split(/\W+/).filter(Boolean); // Removes empty strings

  // Create a Set for fast lookup of lower-cased search words
  const lowerSearchWordsSet = new Set(rawSearchWords);

  if (rawSearchWords.length === 0) return [{ text, isMatch: false }];

  // 2. Construct the regex pattern from the extracted words
  const safePattern = rawSearchWords.map(escapeRegExp).join('|');

  const regex = new RegExp(`(${safePattern})`, 'gi');

  // 3. Split the text and determine match status
  const parts = text.split(regex);
  const result = [];

  for (const part of parts) {
    if (part.length === 0) continue;

    const isMatch = lowerSearchWordsSet.has(part.toLowerCase());

    result.push({
      text: part,
      isMatch: isMatch,
    });
  }

  return result;
}

export function getHighlightedText(text: string, highlight: string): any {
  if (!highlight) return text;
  // Split on highlight term and include term into parts, ignore case
  const parts = splitTextSearchWords(text, highlight);

  return (
    <span>
      {parts.map(({ text, isMatch }, i) => (
        <span key={i} className={isMatch ? styles.highlighted : ''}>
          {text}
        </span>
      ))}
    </span>
  );
}

export function convertCatNameToCatIndex(columnInfo: TpDataTableColumn, catName: string) {
  if (!columnInfo.catConvertorInv) throw getUserError('Column type is not categorical');
  if (!(catName in columnInfo.catConvertorInv)) return NaN;
  return columnInfo.catConvertorInv[catName];
}

export function convertColumnDataToDisplayString(columnInfo: TpDataTableColumn | null, value: any): string {
  if (!columnInfo) return value;
  if (columnInfo.valueType == VALTYPES.NUMBER) {
    if (value === null || value === undefined || isNaN(value)) return '';
    const decimalDigits = columnInfo.decimalDigits;
    if (decimalDigits != null && decimalDigits >= 0) {
      // note: we use the extra parseFloat step to get rid of trailing zeros
      return `${parseFloat(value.toFixed(Math.min(6, decimalDigits)))}`;
    }
  }
  if (columnInfo.valueType == VALTYPES.INTEGER) {
    if (value === null || value === undefined || isNaN(value)) return '';
    return value.toFixed(0);
  }
  if (columnInfo.valueType == VALTYPES.CATEGORICAL) {
    if (value === undefined) return '';
    if (value == PLACEHOLDER_OTHER) return 'Other';
    if (value !== null && value !== undefined) return columnInfo.catConvertor![value];
  }
  if (columnInfo.valueType == VALTYPES.MULTICAT) {
    if (value == PLACEHOLDER_OTHER) return 'Other';
    if (!Array.isArray(value)) return columnInfo.catConvertor![value];
    else return value.map((singleValue: any) => columnInfo.catConvertor![singleValue]).join('; ');
  }
  return String(value);
}

export function convertColumnDataListToDisplayString(columnInfo: TpDataTableColumn, valueList: any[]): string[] {
  return valueList.map((val) => convertColumnDataToDisplayString(columnInfo, val));
}

export function smartCountDisplayString(count: number): string {
  if (count > 10000) return `${(count / 1000).toFixed(0)}k`;
  if (count > 1000) return `${(count / 1000).toFixed(1)}k`;
  return String(count);
}

export function smartFloatDisplayString(value: number): string {
  // converts a float into a string with a set number of significant figures
  const significantFigures = 7;
  if (isNaN(value)) return '---';
  if (Math.abs(value) < 1e-7) return String(value); // for very small values we rely on the standard mechanism
  const magDigits = Math.floor(Math.log10(Math.abs(value)));
  return value.toFixed(Math.max(0, significantFigures - magDigits - 1));
}

export function ChartFilterButton() {
  return (
    <div className={widgetStyles.activeFilter} style={{ color: getSelectionHighlightColor().toCanvasString() }}>
      <FontAwesomeIcon icon="filter" />
    </div>
  );
}

export function generateChartTitle(
  dataContext: TpDataContext,
  dataBinding: TpChartDataBinding,
  config: TpChartConfigParams,
  autoTitle: string,
): string {
  if (config.customTitle) return config.customTitle;
  let title = autoTitle;
  if (dataBinding.filter) {
    const canonicalTypeId = getDataTable(dataContext, dataBinding.dataTableId).canonicalTypeId;
    title += ' - ' + selectionFilter2String(dataContext, canonicalTypeId, dataBinding.filter);
  }
  return title;
}

export function getClippedRenderText(ctx: any, textInp: string, maxWidth: number): string {
  if (ctx.measureText(textInp).width <= maxWidth) return textInp;
  let textClipped = textInp;
  let clippedLength = textClipped.length;
  while (ctx.measureText(textClipped).width > maxWidth && clippedLength > 0) {
    clippedLength--;
    textClipped = textInp.substring(0, clippedLength) + '...';
  }
  return textClipped;
}
